import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { profileService } from '@/services/profile';
import { setProfile } from '@/store/slices/authSlice';
import { profileKeys } from './queryKeys';
import { toast } from 'sonner';
import type { UpdateProfileInput, AvatarUploadOptions } from '@/services/profile/types';

export const useProfileMutations = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const updateMyProfile = useMutation({
    mutationFn: (input: UpdateProfileInput) => profileService.updateMyProfile(input),
    onSuccess: (updatedProfile) => {
      // Update React Query cache
      queryClient.setQueryData(profileKeys.myProfile(), updatedProfile);
      
      // Update Redux store for immediate UI updates
      dispatch(setProfile(updatedProfile));
      
      // Show success message
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const updateUserProfile = useMutation({
    mutationFn: ({ authUserId, input }: { authUserId: string; input: UpdateProfileInput; profileId?: string }) => {
      return profileService.updateUserProfile(authUserId, input);
    },
    onSuccess: (updatedProfile, { authUserId, profileId }) => {
      // Update React Query cache
      queryClient.setQueryData(profileKeys.userProfile(authUserId), updatedProfile);
      
      // Invalidate team member profile cache using the correct profile ID
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', profileId] });
      }
      // Also invalidate with the auth user id as fallback
      queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', authUserId] });
      
      // Invalidate team members list to reflect changes
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      queryClient.invalidateQueries({ queryKey: profileKeys.myProfile() });
      
      // Show success message
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update profile:', error);
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: ({ 
      file, 
      authUserId, 
      companyId, 
      targetAuthUserId,
      options 
    }: { 
      file: File; 
      authUserId: string; 
      companyId: string; 
      targetAuthUserId?: string;
      profileId?: string;
      options?: AvatarUploadOptions;
    }) => profileService.processAvatarUpload(file, authUserId, companyId, targetAuthUserId, options),
    onSuccess: (result, { authUserId, profileId }) => {
      // Show success message
      toast.success('Profile picture uploaded successfully');
      
      // Invalidate profile queries to refetch with new avatar
      queryClient.invalidateQueries({ queryKey: profileKeys.myProfile() });
      queryClient.invalidateQueries({ queryKey: profileKeys.userProfile(authUserId) });
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', profileId] });
      }
      queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', authUserId] });
      
      // Invalidate team members list to reflect avatar changes
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
    },
    onError: (error: Error) => {
      console.error('Failed to upload avatar:', error);
      toast.error(`Failed to upload profile picture: ${error.message}`);
    },
  });

  const deleteAvatar = useMutation({
    mutationFn: ({
      avatarUrl,
      targetAuthUserId,
    }: { avatarUrl: string; targetAuthUserId?: string; profileId?: string }) =>
      profileService.removeAvatar(avatarUrl, targetAuthUserId),
    onSuccess: (_result, { targetAuthUserId, profileId }) => {
      toast.success('Profile picture removed successfully');
      
      // Invalidate profile queries to refetch without avatar
      queryClient.invalidateQueries({ queryKey: profileKeys.myProfile() });
      if (targetAuthUserId) {
        queryClient.invalidateQueries({ queryKey: profileKeys.userProfile(targetAuthUserId) });
        queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', targetAuthUserId] });
      }
      if (profileId) {
        queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', profileId] });
      }
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
    },
    onError: (error: Error) => {
      console.error('Failed to delete avatar:', error);
      toast.error(`Failed to remove profile picture: ${error.message}`);
    },
  });

  return {
    updateMyProfile,
    updateUserProfile,
    uploadAvatar,
    deleteAvatar,
  };
};
